"use client";

import { useSession } from "@/components/SupabaseProvider";
import { ChevronLeftIcon, FolderIcon } from "@heroicons/react/24/outline";
import type { VideoModel } from "@ziggapp/klap-core/video";
import { usePostHog } from "posthog-js/react";
import {
  ComponentPropsWithoutRef,
  FC,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { useFolder } from "../hooks/useFolder";
import { ModalContext } from "../hooks/useModal";
import { useReadPublications } from "../hooks/usePublications";
import { useResources, VideoResource } from "../hooks/useResources";
import { useConditionalTask } from "../hooks/useTask";
import { FOLDER_ID_LENGTH, FolderModel } from "../models/folder_model";
import { PublicationModel } from "../types/models";
import Button from "./Button";
import InfiniteScroll from "./InfiniteScroll";
import Typography from "./Typography";
import VideoCard from "./VideoCard";
import VideoSelectionControls from "./VideoSelectionControls";
import { PublicationHelper } from "../utils/PublicationHelper";
import {
  getSelectStepContinueLabel,
  getSelectStepState,
  pickSelectStepEntryVideoId,
} from "./publicationFlow/selectStep";
import { usePathname, useRouter } from "next/navigation";
import { scheduleHrefForShort, scheduleHrefForVideos } from "./publishFlow";
import {
  buildScheduleHref,
  type ScheduleFrom,
} from "./publicationFlow/route/scheduleUrl";

type Step1ResourceCardProps = {
  resource: FolderModel | VideoResource;
  onSelect: (id: string) => void;
};

// Step 1 renders one card per project. A card is only selectable once the
// project is fully generated (`ready`) AND still has at least one short to
// publish (e.g. it becomes empty once every short is deleted).
const Step1ResourceCard: FC<Step1ResourceCardProps> = ({
  resource,
  onSelect,
}) => {
  const isFolder = resource.id.length === FOLDER_ID_LENGTH;
  const { videos, isLoading: isVideosLoading } = useFolder(
    isFolder ? resource.id : undefined,
  );
  const task = useConditionalTask(resource.taskId ?? "", false);

  const isTaskNotReady = !!task.data && task.data.status !== "ready";
  const isVideoNotReady =
    !isFolder && (resource as VideoResource).status !== "ready";
  const hasNoVideos = isFolder && !isVideosLoading && videos.length === 0;
  const disabled = isTaskNotReady || isVideoNotReady || hasNoVideos;

  const disabledTooltip = hasNoVideos
    ? "This project has no shorts to publish."
    : "This project isn't ready yet.";

  return (
    <div
      className={`relative group rounded-3xl rounded-b-2xl p-3 pb-2 ${
        disabled ? "opacity-50 cursor-default" : "hover:bg-slate-150 cursor-pointer"
      }`}
      onClick={() => {
        if (disabled) return;
        onSelect(resource.id);
      }}
      role="button"
      aria-label={resource.name}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={event => {
        if (!disabled && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          onSelect(resource.id);
        }
      }}
      data-tooltip-content={disabled ? disabledTooltip : undefined}
      data-tooltip-id={disabled ? "global" : undefined}
    >
      <div className="h-full flex flex-col">
        <div className="aspect-video w-full bg-slate-150 overflow-hidden rounded-2xl object-cover flex items-center justify-center relative">
          {resource.id.length === FOLDER_ID_LENGTH &&
          (resource as FolderModel).thumbnailUrl ? (
            // Plain <img>: Remotion's <Img> requires a composition context
            // since 4.0.516 (it now mounts an internal <Sequence> that calls
            // useCurrentFrame()), and this renders in a regular modal.
            <img
              src={(resource as FolderModel).thumbnailUrl!}
              alt={resource.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <FolderIcon className="w-24 h-24 text-slate-300" />
          )}
          <div className="absolute left-2 bottom-2">
            <Typography
              variant="overline"
              className="bg-slate-800 text-white px-2 py-1 rounded-full"
            >
              Shorts
            </Typography>
          </div>
        </div>
        <Typography
          variant="subtitle2"
          className="mt-2 block text-ellipsis whitespace-nowrap overflow-hidden"
        >
          {resource.name}
        </Typography>
      </div>
    </div>
  );
};

type SelectVideoPublicationFormProps = {
  defaultDate: Date;
  defaultStep?: number;
  defaultProjectId?: string;
  defaultSelectedVideoIds?: string[];
  /** Where Continue's `/schedule` session returns to. */
  from?: ScheduleFrom;
  /** yyyy-MM-dd — the calendar month to return to (calendar doors). */
  returnDate?: string | null;
  /** yyyy-MM-dd — calendar day cell entry: the new session's Starting date
   *  defaults to this day. The picker itself is the normal one. */
  startDay?: string | null;
} & ComponentPropsWithoutRef<"div">;

const SelectVideoPublicationForm: FC<SelectVideoPublicationFormProps> = ({
  defaultDate,
  defaultStep = 1,
  defaultProjectId,
  defaultSelectedVideoIds = [],
  from = "results",
  returnDate = null,
  startDay = null,
  ...rest
}) => {
  const modal = useContext(ModalContext);
  const router = useRouter();
  const pathname = usePathname();
  const posthog = usePostHog();
  const session = useSession();
  const resources = useResources(session?.user.id as string);
  const [selectedProjectId, setSelectedProjectId] = useState<
    string | undefined
  >(defaultProjectId);
  const [currentStep, setCurrentStep] = useState(defaultStep);
  const [selectedVideoIds, setSelectedVideoIds] = useState<string[]>(
    defaultSelectedVideoIds,
  );
  const initializedSelectionProjectIdRef = useRef<string | null>(null);
  const step1ScrollParentRef = useRef<HTMLDivElement | null>(null);
  const step2ScrollParentRef = useRef<HTMLDivElement | null>(null);
  const step2VideoCardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const hasAutoScrolledToSelectionRef = useRef(false);

  const { videos, isLoading: isVideosLoading } = useFolder(
    selectedProjectId as string,
  );
  const { data: publications } = useReadPublications(undefined, {
    enabled: currentStep === 2,
  });
  const [isOpeningPublishFlow, startOpeningPublishFlow] = useTransition();

  // The modal lives above the routes, so keep it visible until navigation
  // commits and explicitly close it when the schedule page takes over.
  useEffect(() => {
    if (pathname === "/schedule" && modal.isOpen) modal.close();
  }, [pathname, modal]);

  const { updateHold } = modal;
  useEffect(() => {
    updateHold(isOpeningPublishFlow);
    return () => updateHold(false);
  }, [isOpeningPublishFlow, updateHold]);

  const publicationsByVideoId = useMemo(() => {
    return (publications ?? []).reduce(
      (acc, publication) => {
        if (!publication.projectId) return acc;
        if (!acc[publication.projectId]) {
          acc[publication.projectId] = [];
        }
        acc[publication.projectId].push(publication);
        return acc;
      },
      {} as Record<string, PublicationModel[]>,
    );
  }, [publications]);
  // Shorts holding a committed post: locked tiles (§16). They are the
  // project's plan already and ride into the dialog as context rows, so
  // they never count and never enter as entry videos; with nothing new
  // selected the footer button opens that plan instead.
  const committedVideoIds = useMemo(
    () => PublicationHelper.getCommittedVideoIds(publications),
    [publications],
  );
  const {
    selectableVideos,
    selectedNewVideos,
    areAllSelectableSelected,
    continueKind,
  } = useMemo(
    () =>
      getSelectStepState({
        videos: videos ?? [],
        committedVideoIds,
        selectedVideoIds,
      }),
    [committedVideoIds, selectedVideoIds, videos],
  );
  // Step 2 shows its grid only once both queries are in, so cards never
  // render unselected and then flip when the default selection lands.
  const isStep2Loading =
    isVideosLoading || (currentStep === 2 && publications === undefined);

  useEffect(() => {
    if (!videos) return;
    setSelectedVideoIds((prevSelectedVideoIds) =>
      prevSelectedVideoIds.filter((videoId) =>
        videos.some((video) => video.id === videoId),
      ),
    );
  }, [videos]);

  useEffect(() => {
    if (!selectedProjectId) return;
    if (!videos?.length) return;
    if (initializedSelectionProjectIdRef.current === selectedProjectId) return;

    const hasMismatchedVideos = videos.some(
      (video) => video.folderId && video.folderId !== selectedProjectId,
    );
    if (hasMismatchedVideos) return;

    if (selectedVideoIds.length > 0) {
      initializedSelectionProjectIdRef.current = selectedProjectId;
      return;
    }

    // The default leaves committed shorts out, so it needs the publications:
    // wait for them rather than deciding on the folder alone and never
    // revisiting (the latch stays open until both queries are in).
    if (publications === undefined) return;

    setSelectedVideoIds(
      PublicationHelper.getDefaultScheduleSelection(videos, committedVideoIds),
    );
    initializedSelectionProjectIdRef.current = selectedProjectId;
  }, [
    committedVideoIds,
    publications,
    selectedProjectId,
    selectedVideoIds.length,
    videos,
  ]);

  useEffect(() => {
    if (currentStep === 2) return;
    hasAutoScrolledToSelectionRef.current = false;
  }, [currentStep]);

  useEffect(() => {
    hasAutoScrolledToSelectionRef.current = false;
  }, [selectedProjectId]);

  useEffect(() => {
    if (currentStep !== 2) return;
    if (hasAutoScrolledToSelectionRef.current) return;
    if (isVideosLoading) return;
    if (!videos?.length) return;

    // Reveal the first NEW short about to be scheduled; a locked committed
    // tile is not a selection to scroll to.
    const firstSelectedVideo = selectedNewVideos[0];
    if (!firstSelectedVideo) return;

    const scrollContainer = step2ScrollParentRef.current;
    const targetCard = step2VideoCardRefs.current.get(firstSelectedVideo.id);
    if (!scrollContainer || !targetCard) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    const targetRect = targetCard.getBoundingClientRect();
    const topOffset =
      targetRect.top - containerRect.top + scrollContainer.scrollTop - 16;

    scrollContainer.scrollTo({
      top: Math.max(0, topOffset),
      behavior: "smooth",
    });
    hasAutoScrolledToSelectionRef.current = true;
  }, [currentStep, isVideosLoading, selectedNewVideos, videos]);

  // A day cell's entry carries its day into every new session it opens;
  // an existing plan opened instead keeps its own dates.
  const initialPlacement = startDay ? { day: startDay } : undefined;

  const openShortSchedule = (video: VideoModel) => {
    if (isOpeningPublishFlow || video.status !== "ready") return;
    const href = scheduleHrefForShort({
      video, from, returnDate, initialPlacement,
    });
    startOpeningPublishFlow(() => router.push(href));
  };

  const handleProjectSelect = (projectId: string) => {
    // A standalone short is a project of one: there is nothing to pick, so
    // it opens its own schedule straight away.
    if (projectId.length !== FOLDER_ID_LENGTH) {
      const video = resources.data?.find(resource => resource.id === projectId) as VideoResource | undefined;
      if (video) openShortSchedule(video);
      return;
    }
    initializedSelectionProjectIdRef.current = null;
    setSelectedProjectId(projectId);
    setSelectedVideoIds([]);
    setCurrentStep(2);
  };

  const handleVideoToggle = (videoId: string) => {
    if (isOpeningPublishFlow) return;
    // A committed short's tile is locked: its plan is edited in the dialog,
    // where it rides along as a context row.
    if (committedVideoIds.has(videoId)) return;
    setSelectedVideoIds((prevSelectedVideoIds) => {
      if (prevSelectedVideoIds.includes(videoId)) {
        return prevSelectedVideoIds.filter((id) => id !== videoId);
      }
      return [...prevSelectedVideoIds, videoId];
    });
  };

  const handleToggleAllVideos = () => {
    if (isOpeningPublishFlow || !selectableVideos.length) return;
    setSelectedVideoIds(
      areAllSelectableSelected
        ? []
        : selectableVideos.map((video) => video.id),
    );
  };

  const handleNext = () => {
    if (isOpeningPublishFlow || continueKind === "none") return;
    // Nothing new selected: open the folder's plan instead, on the short
    // the user came from when it is committed, else the one holding the
    // folder's next post, with generation skipped — so the dialog lands on
    // the read face (§12), the project chip's own path.
    const opensPlan = continueKind === "open";
    const entryVideoId = opensPlan
      ? pickSelectStepEntryVideoId({
          publications: publications ?? [],
          videos: videos ?? [],
          committedVideoIds,
          preferredVideoIds: defaultSelectedVideoIds,
        })
      : selectedNewVideos[0]?.id;
    const entryVideo = videos?.find((video) => video.id === entryVideoId);
    if (!entryVideo) return;

    posthog?.capture("Publication Selection Confirmed", {
      selectedCount: selectedNewVideos.length,
      totalCount: videos?.length ?? 0,
      projectId: selectedProjectId,
      opensPlan,
    });

    // Continue is the door into the `/schedule` workspace: create mode
    // over the new shorts, or edit mode on the folder's plan when nothing
    // new is selected. Keep the button loading until the page takes over.
    if (!selectedProjectId) return;
    const href = opensPlan
      ? buildScheduleHref({
          project: selectedProjectId,
          mode: "edit",
          video: entryVideo.id,
          from,
          returnDate,
        })
      : scheduleHrefForVideos({
          folderId: selectedProjectId,
          videoIds: selectedNewVideos.map((video) => video.id),
          focusVideoId: entryVideo.id,
          from,
          returnDate,
          initialPlacement,
        });
    startOpeningPublishFlow(() => router.push(href));
  };

  const renderSkeleton = () => (
    <div className="grid grid-cols-2 md:grid-cols-3 -m-3">
      {Array.from({ length: 6 }).map((_, index) => (
        <div
          key={index}
          className="aspect-video rounded-2xl bg-slate-150 animate-pulse m-3 mb-2"
        />
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="h-full min-h-0 flex flex-col">
      <div className="border-b border-slate-200 bg-white py-4">
        <div className="flex items-center justify-between gap-2 px-4">
          <Button variant="tertiary" size="m" aria-label="Close picker" onClick={() => modal.close()}>
            <ChevronLeftIcon className="w-5 h-5" />
          </Button>
          <Typography variant="subtitle2">Select a project</Typography>
          <div className="w-10" />
        </div>
      </div>
      <div
        ref={step1ScrollParentRef}
        className="flex-1 overflow-y-auto px-4 pt-4 pb-4"
      >
        {resources.isLoading && renderSkeleton()}
        {!resources.isLoading && resources.data?.length === 0 && (
          <div className="flex h-full min-h-48 flex-col items-center justify-center gap-3 px-4 py-8 text-center">
            <FolderIcon className="h-12 w-12 text-slate-400" aria-hidden="true" />
            <Typography variant="subtitle2">
              Create your first video to schedule posts
            </Typography>
            <Typography variant="body2" className="text-slate-500">
              Generate shorts, then choose a project to schedule them.
            </Typography>
            <Button
              size="m"
              onClick={() => {
                modal.close();
                router.push("/spaces");
              }}
            >
              Generate shorts
            </Button>
          </div>
        )}
        {!!resources.data?.length && (
          <InfiniteScroll
            useWindow={false}
            loadMore={() => resources.fetchNextPage()}
            hasMore={resources.hasNextPage}
            initialLoad
            className="grid grid-cols-2 md:grid-cols-3 -m-3"
            loader={
              <>
                {Array.from({ length: 3 }).map((_, index) => (
                  <div
                    key={index}
                    className="aspect-video rounded-2xl bg-slate-200 animate-pulse m-3 mb-2"
                  />
                ))}
              </>
            }
            getScrollParent={() => step1ScrollParentRef.current}
          >
            {resources.data.map((resource) => (
              <Step1ResourceCard
                key={resource.id}
                resource={resource}
                onSelect={handleProjectSelect}
              />
            ))}
          </InfiniteScroll>
        )}
      </div>
    </div>
  );

  const renderVideoCard = (video: VideoModel, index: number) => {
    // Existing plan rows stay checked and locked; only new shorts count.
    const isLocked = committedVideoIds.has(video.id);
    const selected = isLocked || selectedVideoIds.includes(video.id);
    const videoPublications = publicationsByVideoId[video.id] ?? [];

    return (
      <div
        key={video.id}
        className="relative"
        ref={(element) => {
          if (element) {
            step2VideoCardRefs.current.set(video.id, element);
          } else {
            step2VideoCardRefs.current.delete(video.id);
          }
        }}
        aria-disabled={isLocked || isOpeningPublishFlow || undefined}
      >
        <VideoCard
          video={video}
          count={index + 1}
          publications={videoPublications}
          selected={selected}
          dimmed={isLocked}
          role="checkbox"
          aria-label={video.name}
          aria-checked={selected}
          aria-disabled={isLocked || isOpeningPublishFlow}
          tabIndex={isLocked || isOpeningPublishFlow ? -1 : 0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleVideoToggle(video.id);
            }
          }}
          onClick={() => handleVideoToggle(video.id)}
          className={`rounded-3xl rounded-b-2xl p-3 pb-2 ${
            isLocked
              ? "cursor-default"
              : "cursor-pointer hover:bg-slate-150"
          } ${
            selected
              ? "[&>div:first-child]:ring-2 [&>div:first-child]:ring-blue-500 dark:[&>div:first-child]:ring-ring [&>div:first-child]:ring-offset-1 dark:[&>div:first-child]:ring-offset-surface [&>div:first-child]:ring-offset-white"
              : ""
          }`}
        />
      </div>
    );
  };

  const renderStep2 = () => {
    const hasNoNewShorts =
      !isStep2Loading && selectableVideos.length === 0 && continueKind === "open";
    const title = hasNoNewShorts
      ? "No new shorts to schedule"
      : "Select shorts";
    // The controls count the NEW shorts only, and a folder with nothing
    // left to pick shows none: its plan opens through the footer button.
    const showSelectionControls =
      selectableVideos.length > 0 || isStep2Loading;
    const selectionControlsProps = {
      selectedCount: selectedNewVideos.length,
      totalCount: selectableVideos.length,
      areAllSelected: areAllSelectableSelected,
      onToggleAll: handleToggleAllVideos,
      disabled:
        !selectableVideos.length || isStep2Loading || isOpeningPublishFlow,
    };

    return (
      <div className="h-full min-h-0 flex flex-col">
        <div className="border-b border-slate-200 bg-white py-4">
          {/* One row on every width. Mobile has no room for title + count +
              toggle, so the count IS the title once anything is picked (the
              select-mode pattern of Photos apps) and the row is an
              equal-sides grid, the schedule header's title-row geometry:
              the title sits on the true centre while both corners fit their
              share, and a wider corner PUSHES it rather than overlapping it
              (an absolute title touched the toggle at 332px). Desktop keeps
              the flex row: static title, absolutely centred, count beside
              the toggle. */}
          <div className={`relative grid items-center gap-2 px-4 md:flex ${
            hasNoNewShorts
              ? "grid-cols-[2.25rem_minmax(0,1fr)_2.25rem]"
              : "grid-cols-[1fr_auto_1fr]"
          }`}>
            <div className="flex items-center gap-2">
              <Button
                variant="tertiary"
                aria-label="Select a project"
                onClick={() => setCurrentStep(1)}
                disabled={isOpeningPublishFlow}
                size="m"
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </Button>
            </div>
            <Typography
              variant="subtitle2"
              className={`min-w-0 text-center md:hidden ${hasNoNewShorts ? "whitespace-normal" : "truncate"}`}
            >
              {selectedNewVideos.length > 0
                ? `${selectedNewVideos.length} selected`
                : title}
            </Typography>
            <Typography
              variant="subtitle2"
              className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap hidden md:block"
            >
              {title}
            </Typography>
            {showSelectionControls && (
              <VideoSelectionControls
                {...selectionControlsProps}
                containerClassName="ml-auto flex items-center justify-end gap-2"
                countClassName="hidden md:block"
              />
            )}
          </div>
        </div>

        <div
          ref={step2ScrollParentRef}
          className="flex-1 overflow-y-auto px-4 pt-4 pb-4"
        >
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 -m-3">
            {isStep2Loading
              ? Array.from({ length: 10 }).map((_, i) => (
                  <div
                    key={i}
                    className="bg-slate-150 rounded-2xl animate-pulse aspect-[9/16] m-3 mb-2"
                  />
                ))
              : videos?.map(renderVideoCard)}
          </div>
        </div>

        <div className="border-t border-slate-200 p-4">
          <div className="flex-1">
            <div className="flex items-center justify-end gap-2">
              <Button
                disabled={continueKind === "none" || isOpeningPublishFlow}
                loading={isOpeningPublishFlow}
                onClick={handleNext}
                size="m"
                className="w-full sm:w-auto"
              >
                {getSelectStepContinueLabel(continueKind)}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      // Every step wears a back chevron in its header's top-left corner — the
      // same corner the full-bleed Dialog floats its close X in, where it
      // would land on top of the chevron. The chevron is the exit (it closes
      // on step 1), so the X stands down (see `CONTENT_OWNS_CLOSE` in
      // components/Dialog.tsx).
      data-owns-dialog-close=""
      {...rest}
      className={`${rest.className} h-full`}
    >
      {currentStep === 1 && renderStep1()}
      {currentStep === 2 && renderStep2()}
    </div>
  );
};

export default SelectVideoPublicationForm;
